import { useEffect, useState } from "react";import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Chip, StatusPill } from "../lux";
import { Field, SearchBox } from "./pickers";
import AffiliatePicker from "./AffiliatePicker";
import { LeadMatchNotice } from "../LeadMatchDialog";
import { Car, UserCircle, MapTrifold, User, Plus, X, Crown, Wrench } from "@phosphor-icons/react";
import { YES_NO_OPTIONS, INSURED_TYPE_OPTIONS, INSURANCE_POLICY_OPTIONS } from "../../pages/bookingFields";

const TYPES = [
  { key: "rental", label: "Rental", hint: "Self-drive", icon: Car },
  { key: "chauffeur", label: "Chauffeur", hint: "With a driver", icon: UserCircle },
  { key: "tour", label: "Tour", hint: "Itinerary", icon: MapTrifold },
  { key: "internal", label: "Internal", hint: "Hold — no sale", icon: Wrench },
];

const PillGroup = ({ options, value, onChange, testid }) => (
  <div className="flex gap-2 flex-wrap" data-testid={testid}>
    {options.map((o) => (
      <Chip key={o.value} active={value === o.value} onClick={() => onChange(o.value)} testid={`${testid}-${o.value}`}>
        {o.label}
      </Chip>
    ))}
  </div>
);

export default function StepClient({ formType, onTypeChange, form, setForm, errors, ctx }) {
  const { chauffeurLocked, salesOptions, leadMatch } = ctx;
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [searchable, setSearchable] = useState(true);
  const [manual, setManual] = useState(Boolean(form.customer_name));

  // A duplicated booking / restored draft resolves after mount — show the
  // filled-in client fields instead of the empty search state.
  useEffect(() => {
    if (form.customer_name || form.customer_phone || form.customer_email || form.customer_id) setManual(true);
  }, [form.customer_name, form.customer_phone, form.customer_email, form.customer_id]);

  // SALES-021: one effect covers both ways the phone is set — typed, and filled
  // by picking an existing client. The hook debounces, so this does not fire a
  // lookup per keystroke. Depends on `check` (stable) and NOT on the hook's
  // return object, which is new on every render.
  const leadCheck = leadMatch?.check;
  useEffect(() => {
    // SALES-022: an internal hold has no client, so it never asks the lead
    // question — the phone field does not even exist for it.
    if (formType === "internal") return;
    leadCheck?.(form.customer_phone);
  }, [form.customer_phone, leadCheck, formType]);

  useEffect(() => {
    const term = q.trim();
    if (!searchable || term.length < 2) { setMatches([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { search: term } });
        if (!cancel) setMatches((data || []).slice(0, 6));
      } catch {
        if (!cancel) { setSearchable(false); setManual(true); }
      }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, searchable]);

  const setName = (full) => {
    const parts = String(full).trim().split(/\s+/);
    setForm((f) => ({
      ...f,
      customer_name: full,
      customer_first_name: parts[0] || "",
      customer_last_name: parts.slice(1).join(" "),
    }));
  };

  const pick = (c) => {
    const full = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    setForm((f) => ({
      ...f,
      customer_id: c.id || f.customer_id,
      customer_name: full,
      customer_first_name: c.first_name || "",
      customer_last_name: c.last_name || "",
      customer_phone: c.phone || f.customer_phone,
      customer_email: c.email || f.customer_email,
      client_age: c.age || f.client_age,
    }));
    setQ("");
    setMatches([]);
    setManual(true);
  };

  const clearClient = () => {
    setForm((f) => ({ ...f, customer_id: "", customer_name: "", customer_first_name: "", customer_last_name: "", customer_phone: "", customer_email: "", client_age: "" }));
    setManual(false);
    setQ("");
  };

  const types = chauffeurLocked ? TYPES.filter((t) => t.key !== "rental" && t.key !== "internal") : TYPES;

  const typePills = (
    <div>
      <div className="label-mono mb-3">What kind of booking?</div>
      <div className="flex gap-2 flex-wrap" data-testid="wizard-type-pills">
        {types.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onTypeChange(t.key)}
            data-testid={`new-booking-type-${t.key}`}
            aria-pressed={formType === t.key}
            className={`flex items-center gap-2.5 px-5 py-3 rounded-full border transition-colors duration-150 ${
              formType === t.key
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
            }`}
          >
            <t.icon size={17} weight={formType === t.key ? "fill" : "regular"} />
            <span className="text-sm font-medium">{t.label}</span>
            <span className={`text-xs ${formType === t.key ? "opacity-70" : "opacity-60"}`}>{t.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // SALES-022: an internal hold is a wholly different, minimal form — the type
  // pills, who is using the car and why. No client, phone, salesperson, lead
  // notice, affiliate or insurance.
  if (formType === "internal") {
    return (
      <div className="space-y-8" data-testid="wizard-internal-client">
        {typePills}
        <div className="grid grid-cols-1 gap-5 max-w-xl">
          <Field label="Used by" required error={errors.internal_user} hint="Who has the car — a person or a team. Not a client, not billed.">
            <Input value={form.internal_user ?? ""} onChange={(e) => setForm({ ...form, internal_user: e.target.value })} placeholder="e.g. Content team — Alex" data-testid="internal-field-user" />
          </Field>
          <Field label="Reason" hint="What the car is being held for.">
            <Textarea rows={3} value={form.internal_reason ?? ""} onChange={(e) => setForm({ ...form, internal_reason: e.target.value })} placeholder="e.g. Photo shoot for the new SUV listing" className="bg-input border-border" data-testid="internal-field-reason" />
          </Field>
        </div>
        <div className="rounded-2xl border border-slate-500/30 bg-slate-500/[0.06] px-5 py-4 text-sm text-muted-foreground" data-testid="internal-hold-note">
          An internal hold <b className="text-foreground">blocks this car</b> on the calendar for the dates you pick next. It is not a sale — no client, pricing, revenue or salesperson — and it counts toward nothing.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {typePills}

      {/* Client */}
      <div>
        <div className="label-mono mb-3">Client</div>
        {!manual ? (
          <div className="space-y-3">
            {searchable && (
              <SearchBox value={q} onChange={setQ} placeholder="Search existing clients by name, phone or email…" testid="wizard-client-search" />
            )}
            {matches.length > 0 && (
              <div className="border border-border bg-card divide-y divide-border/60 overflow-hidden" data-testid="client-suggestions">
                {matches.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pick(c)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition-colors"
                    data-testid={`client-suggestion-${c.id}`}
                  >
                    <span className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
                      <User size={15} className="text-muted-foreground" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-heading text-sm truncate">{c.first_name} {c.last_name}</span>
                      <span className="block text-xs text-muted-foreground truncate">{[c.phone, c.email].filter(Boolean).join(" · ") || "—"}</span>
                    </span>
                    {(c.vip_tier === "gold" || c.vip_tier === "platinum") && (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/12 border border-primary/25 text-primary text-[10px] uppercase tracking-wider">
                        <Crown size={10} weight="fill" /> {c.vip_tier}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => { setManual(true); if (q.trim()) setName(q.trim()); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              data-testid="wizard-new-client"
            >
              <Plus size={14} /> {q.trim() ? `New client “${q.trim()}”` : "New client"}
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Client name" required error={errors.customer_name}>
                <div className="relative">
                  <Input
                    value={form.customer_name ?? ""}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    autoComplete="off"
                    data-testid="bookings-field-customer_name"
                  />
                  <button type="button" onClick={clearClient} title="Search a different client" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="wizard-client-clear">
                    <X size={14} />
                  </button>
                </div>
              </Field>
              <Field label="Phone" required error={errors.customer_phone}>
                <Input type="tel" value={form.customer_phone ?? ""} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="(416) 000-0000" data-testid="bookings-field-customer_phone" />
                {/* SALES-021: the lead question, asked here instead of at save.
                    Inline, but not ignorable — an unanswered match holds
                    Continue, and both answers are real buttons. */}
                {ctx.leadMatch && <LeadMatchNotice {...ctx.leadMatch.inline} />}
              </Field>
              <Field label="Email">
                <Input type="email" value={form.customer_email ?? ""} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="name@email.com" data-testid="bookings-field-customer_email" />
              </Field>
              {formType === "rental" && (
                <>
                  <Field label="Age" required error={errors.client_age}>
                    <Input value={form.client_age ?? ""} onChange={(e) => setForm({ ...form, client_age: e.target.value })} placeholder="e.g. 28" data-testid="bookings-field-client_age" />
                  </Field>
                  <Field label="Credit card" required error={errors.credit_card} className="sm:col-span-2">
                    <Input value={form.credit_card ?? ""} onChange={(e) => setForm({ ...form, credit_card: e.target.value })} placeholder="Card on file / last 4" data-testid="bookings-field-credit_card" />
                  </Field>
                </>
              )}
            </div>

            {formType === "rental" && (
              <div className="space-y-4 border border-border rounded-2xl p-5 bg-white/[0.015]">
                <Field label="Insurance coverage" required error={errors.insurance_coverage}>
                  <PillGroup options={YES_NO_OPTIONS} value={form.insurance_coverage} onChange={(v) => setForm({ ...form, insurance_coverage: v })} testid="bubble-insurance_coverage" />
                </Field>
                {form.insurance_coverage === "yes" && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
                    <Field label="Insurance called">
                      <PillGroup options={YES_NO_OPTIONS} value={form.insurance_called} onChange={(v) => setForm({ ...form, insurance_called: v })} testid="bubble-insurance_called" />
                    </Field>
                    <Field label="Insured">
                      <PillGroup options={INSURED_TYPE_OPTIONS} value={form.insured_type} onChange={(v) => setForm({ ...form, insured_type: v })} testid="bubble-insured_type" />
                    </Field>
                    <Field label="Insurance type">
                      <PillGroup options={INSURANCE_POLICY_OPTIONS} value={form.insurance_policy_type} onChange={(v) => setForm({ ...form, insurance_policy_type: v })} testid="bubble-insurance_policy_type" />
                    </Field>
                    <Field label="Insurance notes" className="sm:col-span-3">
                      <Input value={form.insurance_notes ?? ""} onChange={(e) => setForm({ ...form, insurance_notes: e.target.value })} placeholder="Policy details, adjuster, claim #…" data-testid="bookings-field-insurance_notes" />
                    </Field>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Salesperson */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Salesperson" required error={errors.salesperson} hint={chauffeurLocked ? "Your name is stamped on submissions automatically." : undefined}>
          <Select
            value={salesOptions.includes(form.salesperson) ? form.salesperson : (form.salesperson ? "__other__" : "")}
            onValueChange={(v) => setForm({ ...form, salesperson: v === "__other__" ? " " : v })}
          >
            <SelectTrigger data-testid="bookings-field-salesperson"><SelectValue placeholder="Select salesperson…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {salesOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              <SelectItem value="__other__">Other…</SelectItem>
            </SelectContent>
          </Select>
          {form.salesperson && !salesOptions.includes(form.salesperson) && (
            <Input
              autoFocus
              value={form.salesperson.trim()}
              onChange={(e) => setForm({ ...form, salesperson: e.target.value || " " })}
              placeholder="Type the salesperson's name"
              className="mt-2"
              data-testid="bookings-field-salesperson-other"
            />
          )}
        </Field>
        {String(form.salesperson ?? "").trim().toLowerCase() === "affiliate" && (
          <Field label="Which affiliate?" required error={errors.affiliate_name} hint="Credits this booking on the affiliate leaderboard">
            <AffiliatePicker value={form.affiliate_name} onChange={(v) => setForm({ ...form, affiliate_name: v })} />
          </Field>
        )}
        {form.customer_id && (
          <div className="flex items-end">
            <StatusPill status="existing client" tone="green">Existing client linked</StatusPill>
          </div>
        )}
      </div>
    </div>
  );
}
